import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Icon,
  Search,
  TextBody,
} from '@pypestream/design-system';

import { GenericModal, GenericModalProps } from '../../modals';
import {
  localeAvailability,
  localeOptions,
  type LanguageCatalogEntry,
  type LocaleUnavailableReason,
  type SpeechRecognition,
} from '../language-catalog';

/*
 * Layout and type from the Figma "Pro Studio (Production)" Add a language
 * modal (node 4273:11060): the large modal (800px, 60px padding), an H4 title
 * beside a 240px search, 40px rows split by Gray/200 rules, a "Google only"
 * pill with the Google mark, and a purple Add beside an outlined Cancel.
 */

const UNAVAILABLE_HINT: Record<LocaleUnavailableReason, string> = {
  'google-only': 'Needs Google speech recognition',
  'english-only-model': 'This speech model is English only',
  'language-taken': 'Already added',
};

/** Off-screen but read by assistive tech. */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

const STYLES = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
  },
  title: {
    margin: 0,
    fontSize: 24,
    lineHeight: '36px',
    fontWeight: 500,
    color: '#151a25',
    whiteSpace: 'nowrap',
  },
  list: {
    // Positioned, so the visually hidden hints (absolute) are laid out and
    // clipped inside the list's own scroll instead of stretching the modal
    // body into a second scrollbar.
    position: 'relative',
    marginTop: 32,
    // Only the list scrolls, as in the design: capped so the modal (60px
    // padding, header, footer, gaps ≈ 300px, plus its own margin) always fits
    // the viewport — otherwise the modal body scrolls too.
    maxHeight: 'min(448px, calc(100vh - 360px))',
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    // A 40px cell with 12px either side of the rule, as in the design.
    boxSizing: 'content-box',
    minHeight: 40,
    padding: '12px 0',
    borderBottom: '1px solid #dfe1e5',
  },
  rowLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  name: {
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 500,
    color: '#151a25',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  googlePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '4px 8px',
    borderRadius: 100,
    background: 'rgba(21, 26, 37, 0.05)',
    fontSize: 11,
    lineHeight: '16px',
    color: '#151a25',
    whiteSpace: 'nowrap',
  },
  checkboxCell: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  footer: {
    marginTop: 32,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  addButton: {
    height: 40,
    padding: '0 20px',
    border: 'none',
    borderRadius: 8,
    background: '#8573e7',
    color: '#ffffff',
    fontSize: 14,
    lineHeight: '24px',
    fontWeight: 500,
    fontFamily: 'inherit',
  },
} satisfies Record<string, CSSProperties>;

export interface AddLanguageModalProps extends GenericModalProps {
  catalog: LanguageCatalogEntry[];
  speechRecognition: SpeechRecognition;
  /** Language keys already on the channel. */
  takenLanguages: string[];
  onAdd: (locales: string[]) => void;
}

/**
 * "Add a language" (PSTUDIO-2950). Lists locales, not languages, so a channel
 * can carry es-ES rather than the default es-US — but only one locale per
 * language, because the overlay is keyed by language. Checking a locale
 * therefore disables its siblings, the same way one already on the channel
 * does. Why a locale is unavailable is not shown in the design; it is given
 * to assistive tech through the checkbox's description.
 */
export const AddLanguageModal: FC<AddLanguageModalProps> = ({
  modalRef,
  onClose,
  catalog,
  speechRecognition,
  takenLanguages,
  onAdd,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const options = useMemo(() => localeOptions(catalog), [catalog]);

  const selectedLanguages = useMemo(
    () =>
      options.filter((o) => selected.includes(o.locale)).map((o) => o.language),
    [options, selected]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(term) ||
            o.locale.toLowerCase().includes(term)
        )
      : options;
  }, [options, search]);

  const close = () => {
    setSearch('');
    setSelected([]);
    onClose?.();
  };

  const toggle = (locale: string, checked: boolean) =>
    setSelected((prev) =>
      checked ? [...prev, locale] : prev.filter((l) => l !== locale)
    );

  const canAdd = selected.length > 0;

  // ps-modal moves its content into a portal outside the React root, so a
  // React onClick on a native element inside it never fires (the design
  // system's own controls attach native listeners through @lit/react). The
  // Add button therefore listens natively, reading the latest state by ref.
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<() => void>(() => undefined);
  confirmRef.current = () => {
    if (!selected.length) return;
    onAdd(selected);
    close();
  };
  useEffect(() => {
    const button = addButtonRef.current;
    if (!button) return undefined;
    const confirm = () => confirmRef.current();
    button.addEventListener('click', confirm);
    return () => button.removeEventListener('click', confirm);
  }, []);

  return (
    <GenericModal
      modalRef={modalRef}
      onClose={close}
      size="large"
      test-id="ps-voice-add-language-modal"
    >
      <div style={STYLES.header}>
        <h2 style={STYLES.title}>Add a language</h2>
        <Search
          placeholder="Search"
          value={search}
          onInput={(e) => setSearch(e.target.value)}
          test-id="ps-voice-add-language-search"
          size="large"
          style={{ width: 240, flexShrink: 0 }}
        />
      </div>

      <div style={STYLES.list} role="list" aria-label="Languages">
        {visible.map((option) => {
          const isSelected = selected.includes(option.locale);
          // A locale selected in this modal counts as taken for its siblings,
          // but never for itself — it has to stay uncheckable.
          const taken = [
            ...takenLanguages,
            ...selectedLanguages.filter(() => !isSelected),
          ];
          const availability = localeAvailability(
            option,
            speechRecognition,
            taken
          );
          const disabled = !availability.enabled;
          const hintId = `ps-voice-add-language-hint-${option.locale}`;

          return (
            <div
              key={option.locale}
              role="listitem"
              style={STYLES.row}
              {...{
                'test-id': `ps-voice-add-language-option-${option.locale}`,
              }}
            >
              <div style={STYLES.rowLabel}>
                <span style={STYLES.name}>{option.label}</span>
                {option.googleOnly && (
                  <span style={STYLES.googlePill}>
                    <Icon name="google" size="xsmall" aria-hidden="true" />
                    Google only
                  </span>
                )}
                {!availability.enabled && (
                  <span id={hintId} style={VISUALLY_HIDDEN}>
                    {UNAVAILABLE_HINT[availability.reason]}
                  </span>
                )}
              </div>
              <div style={STYLES.checkboxCell}>
                <Checkbox
                  size="small"
                  checked={isSelected}
                  disabled={disabled}
                  aria-label={option.label}
                  aria-describedby={disabled ? hintId : undefined}
                  test-id={`ps-voice-add-language-checkbox-${option.locale}`}
                  onChange={(e) => toggle(option.locale, e.target.checked)}
                />
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <TextBody variant="secondary" textAlign="center">
            No languages match “{search}”
          </TextBody>
        )}
      </div>

      <div style={STYLES.footer}>
        <Button
          size="large"
          variant="secondary"
          onClick={close}
          test-id="ps-voice-add-language-cancel-button"
        >
          Cancel
        </Button>
        {/* The design's Add is Purple/300; the design system's primary button
            is gray and exposes no colour override, so this one is native. */}
        <button
          type="button"
          ref={addButtonRef}
          disabled={!canAdd}
          {...{ 'test-id': 'ps-voice-add-language-confirm-button' }}
          style={{
            ...STYLES.addButton,
            opacity: canAdd ? 1 : 0.5,
            cursor: canAdd ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </button>
      </div>
    </GenericModal>
  );
};
